<script lang="ts" setup>import { computed as _computed } from 'vue';

import type { Category } from '@consumer/logic/v2/header'
import RivePreview from '@shared/components/Rive/canvas/RivePreview.vue'

withDefaults(defineProps<{
  category: Category
  isActive?: boolean
  gaTrackingString?: string
  dataQAString?: string | undefined
  iconLayout?: 'row' | 'col'
}>(), { iconLayout: 'col', })
const hasGradient = !!__props.category.gradientName

const isGradient = !!hasGradient && !__props.category.inactiveColorName

const inactiveColorName = _computed(() => {
  if (__props.category.inactiveColorName)
    return {
      '--inactive-header-icon-color': `var(${__props.category.inactiveColorName})`,
    }
  else if (isGradient)
    return {
      '--inactive-header-icon-color': 'transparent',
    }
  else return {}
})

const gradientColorName = _computed(() => {
  if (isGradient)
    return {
      '--header-text-gradient-color': `var(${__props.category.gradientName})`,
    }
  else
    return {}
})

// ...more code
</script>

<template>
  <component
    :is="category.path ? 'a' : 'button'"
    :href="category.path"
    :rel="category.rel"
    :class="[
      `flex items-center group block
      active:text-navy hover:text-navy
      outline-none category-button`,
      iconLayout === 'row' ? 'flex-row place-items-center gap-[6px]' : 'flex-col gap-3',
      iconLayout === 'row' ? 'px-[15px]' : 'flex-col gap-3',
      dataQAString
    ]"
    :style="[
      iconLayout === 'row' ? {} : {width: '96px'},
      inactiveColorName,
      gradientColorName,
      category.activeColorName ? {'--active-header-icon-color': `var(${category.activeColorName})`}: {},
    ]"
    :data-track-ga="gaTrackingString"
    :data-qa-string="dataQAString"
    :data-is-active="isActive"
  >
    <div
      v-if="category.riveUrl || category.icon"
      class="w-5 h-5 lg:w-6 lg:h-6"
      :class="{
        'static-icon': !category.riveUrl,
        '-mt-[6px]': iconLayout === 'row'
      }"
    >
      <RivePreview
        v-if="category.riveUrl"
        :riveUrl="category.riveUrl"
        stateMachine="mainSM"
        artboard="mainArtboard"
        class="w-full h-full"
        :href="category.path"
      >
        <template #fallback>
          <GIcon :icon="category.icon" style=" height: 100%;width: 100%;"/>
        </template>
        <template #loading>
          <GIcon :icon="category.icon" style=" height: 100%;width: 100%;"/>
        </template>
      </RivePreview>
      <GIcon
        v-else
        :gradientColorName="gradientColorName['--header-text-gradient-color']"
        :icon="category.icon"
        :style="{
          height: '100%',
          width: iconLayout=== 'row'? '19px': '100%'
        }"
      />
    </div>
    <div
      :class="[
        `inline-block leading-none font-semibold whitespace-nowrap
        underline-2 underline-v2-active underline-offset-8
        group-active:underline lg:group-hover:underline
        text-center text-wrapper pb-2`,
        iconLayout === 'row' && category.icon ? '' : 'w-full',
        isActive ? 'underline' : '',
      ]"
      :style="{
        fontSize: iconLayout === 'row' ? '0.875rem' : '0.75rem',

      }"
    >
      {{ category.title }}
    </div>
  </component>
</template>
<style lang="scss" scoped>
.category-button {
  color: var(--inactive-header-icon-color, currentColor);

  .text-wrapper {
    background-clip: text;
    background-image: var(--header-text-gradient-color);
    color: var(--inactive-header-icon-color, transparent);
  }

  &[data-is-active="true"] {
    color: var(--active-header-icon-color, currentColor);
  }
}

.gradient-mask {
  /* Fallback background if gradient fails */
  background-color: currentcolor;
  background-image: var(--header-text-gradient-color);

  /* Apply the mask using the custom property from our inline style */
  mask-image: var(--mask-image-url);
  mask-position: center;
  mask-repeat: no-repeat;

  /* Ensure the mask scales correctly */
  mask-size: contain;
}

.static-icon {
  padding: 1px;
}

</style>
